import type {
  AvailablePaymentMethods,
  DefaultValuesOption,
  PaymentMethod as StripePaymentMethod,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  PaymentIntent,
} from '@stripe/stripe-js';
import type { AvailablePaymentMethod, Customer, PaymentKeys, PaymentMethod } from '@/types/checkout';
import { PaymentMethodEnum, ProviderEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { BLIK_CODE_LENGTH } from '@/constants/payment';
import { EXPRESS_FREE_SHIPPING_RATES, stripeApmPaymentElementAddressMethods } from '@/constants/stripe';
import type { FeatureFlag } from '@/constants/featureFlags';
import { shouldUseWalletDerivedZipTax } from '@/utils/tax';
import { getApplePayTotalLabel } from '@/utils/adyen';
import { getCustomerCountry } from '@/utils/customer';
import { getPaymentKey } from '@/utils/getPaymentKey';

const BLIK_CODE_REGEX = new RegExp(`^\\d{${BLIK_CODE_LENGTH}}$`);

export const isBlikCodeValid = (code: string): boolean => BLIK_CODE_REGEX.test(code);

/**
 * `runPayment` resolves `undefined` when it caught (and surfaced) a failure —
 * it never rethrows. Express forms use this to decide whether the wallet
 * sheet still needs to be told about the failure.
 */
export const isExpressPaymentFailure = (runPaymentResult: unknown): boolean => runPaymentResult === undefined;

/**
 * Closes the Apple / Google Pay sheet in a failure state.
 *
 * Stripe's Express Checkout Element keeps the wallet sheet open until the
 * payment is confirmed or `event.paymentFailed()` is called. When the confirm
 * path throws before Stripe completes the session (e.g. `createPaymentMethod`
 * rejected), nothing else settles the sheet and it spins until the wallet's
 * own ~30s timeout — the "infinite loading" the shopper sees while our page
 * already shows the error underneath. Stripe only honours the `{ reason }`
 * shape here.
 */
export const failExpressCheckoutSheet = (event: StripeExpressCheckoutElementConfirmEvent): void => {
  event.paymentFailed?.({ reason: 'fail' });
};

export const stripeApmRequiresPaymentElementAddress = (
  paymentMethod: PaymentMethodEnum | ExcludeOtherPaymentMethod | undefined
): boolean =>
  paymentMethod != null && stripeApmPaymentElementAddressMethods.includes(paymentMethod as PaymentMethodEnum);

/** Maps saved customer fields to `<PaymentElement options.defaultValues>`. */
export const getStripePaymentElementDefaultValues = (
  customer: Customer | null | undefined,
  paymentMethod?: PaymentMethodEnum | ExcludeOtherPaymentMethod
): DefaultValuesOption => {
  const billingDetails: NonNullable<DefaultValuesOption['billingDetails']> = {
    name: customer?.name ?? '',
  };

  if (stripeApmRequiresPaymentElementAddress(paymentMethod)) {
    const country = getCustomerCountry(customer);
    const line1 = customer?.address?.addressLine1 || customer?.address?.address || '';

    billingDetails.address = {
      line1,
      line2: customer?.address?.addressLine2 ?? '',
      city: customer?.address?.city,
      state: customer?.address?.state,
      postal_code: customer?.address?.zipCode,
      ...(country ? { country } : {}),
    };
  }

  return { billingDetails };
};

export const buildStripeGenericPaymentRequest = (
  stripePaymentMethod: StripePaymentMethod,
  paymentMethodSettings: PaymentMethod,
  customer: Customer
) => {
  return {
    paymentProvider: ProviderEnum.STRIPE,
    receipt_email: stripePaymentMethod?.billing_details.email ?? customer.email,
    params: {
      paymentMethodId: stripePaymentMethod?.id,
      paymentMethodToken: stripePaymentMethod?.id,
    },
    paymentMethodDisplayName: paymentMethodSettings.paymentMethodDisplayName,
    paymentMethodId: paymentMethodSettings.id,
  };
};

export const buildStripeBlikPaymentRequest = (paymentMethodSettings: PaymentMethod, customer: Customer) => {
  return {
    paymentProvider: ProviderEnum.STRIPE,
    receipt_email: customer.email,
    params: {},
    paymentMethodDisplayName: paymentMethodSettings.paymentMethodDisplayName,
    paymentMethodId: paymentMethodSettings.id,
  };
};

export const getStripePaymentKeyBySelectedPaymentMethod = (
  paymentMethods: PaymentMethod[],
  selectedPaymentMethod: ExcludeOtherPaymentMethod,
  paymentKeys: PaymentKeys
) => {
  const stripePaymentMethod = paymentMethods.find((pm) => pm.paymentMethodName === selectedPaymentMethod);
  return getPaymentKey(stripePaymentMethod?.pspSubMidPk, paymentKeys.stripe, {
    provider: ProviderEnum.STRIPE,
    paymentMethod: selectedPaymentMethod,
  });
};

export const getStripePaymentKey = (paymentKeys: PaymentKeys, paymentMethods: PaymentMethod[]) => {
  const cardMethod = paymentMethods.find(
    (paymentMethod) =>
      paymentMethod.provider === ProviderEnum.STRIPE &&
      paymentMethod.paymentMethodName === PaymentMethodEnum.CREDIT_CARD
  );
  return getPaymentKey(cardMethod?.pspSubMidPk, paymentKeys.stripe, {
    provider: ProviderEnum.STRIPE,
    paymentMethod: PaymentMethodEnum.CREDIT_CARD,
  });
};

export const getStripeErrorMessage = (
  t: (key: string) => string,
  error: { type: string; code?: string; message?: string } | null,
  fieldType: 'number' | 'expiry' | 'cvc'
): string | null => {
  if (!error) return null;

  const errorCode = (error as { code?: string }).code || error.type;

  switch (fieldType) {
    case 'number':
      if (errorCode === 'incomplete_number' || errorCode === 'incomplete') {
        return t('fieldErrors.incompleteCard');
      }
      if (errorCode === 'invalid_number' || errorCode === 'incorrect_number') {
        return t('fieldErrors.invalidCard');
      }
      return t('fieldErrors.invalidCard');
    case 'expiry':
      if (errorCode === 'incomplete_expiry' || errorCode === 'incomplete') {
        return t('fieldErrors.incompleteExpirationDate');
      }
      if (errorCode === 'invalid_expiry_year' || errorCode === 'expired_card') {
        return t('fieldErrors.invalidExpirationDate');
      }
      return t('fieldErrors.incompleteExpirationDate');
    case 'cvc':
      if (errorCode === 'incomplete_cvc' || errorCode === 'incomplete') {
        return t('fieldErrors.incompleteSecurityCode');
      }
      if (errorCode === 'invalid_cvc') {
        return t('fieldErrors.invalidSecurityCode');
      }
      return t('fieldErrors.incompleteSecurityCode');
    default:
      return null;
  }
};

/**
 * Fields shared by a Stripe `StripeError` and a PaymentIntent's
 * `last_payment_error` (`code`/`decline_code`/`type`) — enough to feed
 * `stripeService.mapStripeErrorCode`. Kept structural rather than importing
 * both SDK types so either shape can be passed as-is.
 */
interface StripeDeclineReasonSource {
  decline_code?: string;
  code?: string;
  type?: string;
}

/**
 * The reason string to hand `stripeService.mapStripeErrorCode`, preferring
 * the issuer's `decline_code` over the broader `code`/`type`. Falls back from
 * a top-level `StripeError` (e.g. from `elements.submit()` /
 * `handleNextAction`) to the PaymentIntent's `last_payment_error`, which is
 * where the decline detail lands when the SDK call resolves without an error
 * but the issuer declined the charge underneath.
 */
export const getStripeDeclineReason = (
  error?: StripeDeclineReasonSource | null,
  lastPaymentError?: StripeDeclineReasonSource | null
): string | undefined =>
  error?.decline_code ?? error?.code ?? error?.type ?? lastPaymentError?.decline_code ?? lastPaymentError?.type;

export const isStripeExpressPaymentMethodExists = (
  paymentMethods: AvailablePaymentMethod[],
  paymentMethodName: PaymentMethodEnum
) => {
  return !!paymentMethods.find(
    (pm) => pm.paymentMethodName === paymentMethodName && pm.provider === ProviderEnum.STRIPE && pm.isExpress
  );
};

/**
 * Stripe's per-wallet availability map → our canonical method names. Unmapped
 * Stripe keys are dropped: the Express Checkout Element only ever paints the
 * wallets we configured it with.
 */
const STRIPE_WALLET_TO_PAYMENT_METHOD: Partial<Record<keyof AvailablePaymentMethods, PaymentMethodEnum>> = {
  applePay: PaymentMethodEnum.APPLE_PAY,
  googlePay: PaymentMethodEnum.GOOGLE_PAY,
  link: PaymentMethodEnum.LINK,
  amazonPay: PaymentMethodEnum.AMAZON_PAY,
  paypal: PaymentMethodEnum.PAYPAL,
};

/**
 * Which wallets one Express Checkout Element actually painted, from the map it
 * reports on `onReady`. The element serves several wallets behind a single
 * OR-ed availability boolean, so callers that need to know WHICH ones resolved
 * (CHECKOUT_PRESENTED's `payment_methods`) have to read this instead.
 *
 * A missing map means Stripe had nothing to show — the documented shape when
 * no payment method can render — so it resolves to nothing available rather
 * than to an unknown.
 */
export const getStripeExpressAvailableMethods = (methods?: AvailablePaymentMethods): PaymentMethodEnum[] => {
  if (!methods) return [];
  return Object.entries(STRIPE_WALLET_TO_PAYMENT_METHOD)
    .filter(([stripeKey]) => methods[stripeKey as keyof AvailablePaymentMethods])
    .map(([, paymentMethod]) => paymentMethod);
};

/**
 * ACDEV-25067: gate `shippingAddressRequired` (and its mandatory companion
 * `shippingRates`) on whether we actually need the wallet ZIP for a tax
 * recalculation. Non-US/CA customers OR FF-OFF sessions get neither, so the
 * Apple/Google Pay sheet never shows a shipping section or a "Free shipping"
 * row, and the `shippingaddresschange` event does not fire — no wasted
 * callback trip. Stays in lockstep with `useStripeWalletShippingZipTax`'s
 * callback gate so we never install `shippingAddressRequired` without a
 * listener (which would hang the sheet on "loading").
 */
export const getStripeExpressCheckoutShippingConfig = (
  customer: Customer,
  featureFlags: Record<FeatureFlag, boolean>,
  sessionStartedWithoutValidTax: boolean
): Pick<StripeExpressCheckoutElementOptions, 'shippingAddressRequired' | 'shippingRates'> => {
  if (!shouldUseWalletDerivedZipTax(customer, featureFlags, sessionStartedWithoutValidTax)) return {};
  return {
    shippingAddressRequired: true,
    shippingRates: EXPRESS_FREE_SHIPPING_RATES,
  };
};

/**
 * ACDEV-26742 (Stripe parity with Adyen): brand the wallet sheet with the
 * publisher company name via ECE's `business.name` — Stripe's documented
 * mechanism for "who they're paying" on the Apple Pay sheet
 * (docs.stripe.com/js/elements_object/create_express_checkout_element).
 *
 * DISPLAY-ONLY: `business.name` feeds the on-device payment interface. It is
 * not part of the PaymentMethod the FE creates nor of the PaymentIntent the
 * BE confirms — the bank statement descriptor stays owned by the Stripe
 * account settings / server-side `statement_descriptor(_suffix)`.
 *
 * `billingAddressRequired: true` is pinned deliberately: per Stripe's docs,
 * passing `business` (or `applePay`/`phoneNumberRequired`/
 * `allowedShippingCountries`) at create time flips the
 * `billingAddressRequired` DEFAULT from true to false. Without the pin,
 * enabling the branding flag would silently stop collecting the billing
 * address, degrading AVS data and potentially approval rates. With the pin,
 * collected payment data is byte-identical to the flag-OFF path.
 *
 * Gated on `APPLE_PAY_COMPANY_NAME_LABEL` via `getApplePayTotalLabel` (the
 * same normalization the Adyen path uses): flag OFF or a blank/missing name
 * returns `{}` — pre-flag options, Stripe falls back to the account's
 * registered business name.
 */
export const getStripeExpressCheckoutBusinessConfig = (
  companyName: string | undefined,
  featureFlags: Record<FeatureFlag, boolean>
): Pick<StripeExpressCheckoutElementOptions, 'business' | 'billingAddressRequired'> => {
  const name = getApplePayTotalLabel(companyName, featureFlags);
  if (!name) return {};
  return {
    business: { name },
    billingAddressRequired: true,
  };
};

/**
 * Runtime shape for Stripe `next_action` fields that carry a hosted redirect
 * URL. `@stripe/stripe-js` only types `redirect_to_url`; Alipay (and similar
 * APMs) expose `alipay_handle_redirect` at runtime instead.
 */
export type StripeNextActionWithRedirectUrl = PaymentIntent.NextAction & {
  alipay_handle_redirect?: { url?: string | null } | null;
};

/**
 * Hosted authorization URL from a PaymentIntent `next_action`, if any.
 * Prefers the classic `redirect_to_url` shape, then Alipay's
 * `alipay_handle_redirect` (untyped in `@stripe/stripe-js`).
 */
export const getStripeNextActionRedirectUrl = (
  nextAction: StripeNextActionWithRedirectUrl | null | undefined
): string | null | undefined => nextAction?.redirect_to_url?.url ?? nextAction?.alipay_handle_redirect?.url;
